import { ErpDiv, ErpSpinner } from "@/components/erp";
import React from 'react'
import { Navigate, Outlet, useLocation } from 'react-router-dom'
import { useAppSelector } from '@/redux/hooks'
import { isTenantSuspended, isTenantExpired } from '@/services/tokenStorage'
import type { Permission } from '@/enums/Permission'

interface RouteGuardProps {
  authRequired?: boolean
  requiresSuperAdmin?: boolean
  permission?: Permission | string | (Permission | string)[]
}

export const RouteGuard: React.FC<RouteGuardProps> = ({
  authRequired,
  requiresSuperAdmin,
  permission,
}) => {
  const location = useLocation()
  const { isAuthenticated, isSuperAdmin, Permissions } = useAppSelector((state) => state.auth)

  const tenantSuspended = isTenantSuspended()
  const tenantExpired = isTenantExpired()

  // Tenant status only matters for an authenticated session: an anonymous
  // visitor must reach /login first, never the tenant-suspended page.
  if (isAuthenticated && tenantSuspended && location.pathname !== '/tenant-suspended') {
    return <Navigate to="/tenant-suspended" replace />
  }

  if (isAuthenticated && tenantExpired && location.pathname !== '/tenant-expired') {
    return <Navigate to="/tenant-expired" replace />
  }

  if (authRequired && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  // `requiresSuperAdmin` implies an authenticated session even when the caller
  // forgot `authRequired`, so an anonymous visitor is sent to /login.
  if (requiresSuperAdmin && !isAuthenticated) {
    return <Navigate to="/login" state={{ from: location }} replace />
  }

  if (requiresSuperAdmin && !isSuperAdmin) {
    return <Navigate to="/app/dashboard" replace />
  }

  // `Permissions` can be undefined (older persisted `persist:root` snapshots
  // or an unset slice), not just null — treat both as "not loaded yet"
  // instead of falling through and granting access.
  if (permission && !Permissions) {
    return <ErpDiv className="flex items-center justify-center min-h-screen"><ErpSpinner variant="primary" /></ErpDiv>;
  }

  // A malformed persisted value must deny, never throw inside render.
  if (permission && Array.isArray(Permissions)) {
    const hasPermission = Array.isArray(permission)
      ? permission.some((p) => Permissions.includes(p))
      : Permissions.includes(permission);
    if (!hasPermission) {
      return <Navigate to="/not-authorized" replace />
    }
  }

  return <Outlet />
}

export default RouteGuard
